import React, { useState, useEffect } from 'react';
import { Wallet, LogIn, Loader2, AlertCircle, User } from 'lucide-react';
import { authService } from '../services/authService';

interface LoginFormProps {
  onLoginSuccess: (username: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger stagger mount
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    setIsLoading(true);
    setError('');
    try {
      const isValid = await authService.login(username, password);
      if (isValid) {
        onLoginSuccess(username);
      } else {
        setError('ユーザー名またはパスワードが間違っています。');
        setShake(true);
        setTimeout(() => setShake(false), 600);
      }
    } catch (err) {
      console.error(err);
      setError('エラーが発生しました。');
      setShake(true);
      setTimeout(() => setShake(false), 600);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 paper-bg relative overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute top-12 right-8 w-40 h-40 rounded-full opacity-25 animate-float-slow"
        style={{background:'var(--forest)', filter:'blur(48px)'}} />
      <div className="absolute bottom-16 left-4 w-32 h-32 rounded-full opacity-20 animate-float"
        style={{background:'var(--terra)', filter:'blur(36px)', animationDelay:'1.2s'}} />
      <div className="absolute top-1/2 left-1/2 w-72 h-72 rounded-full opacity-5 animate-spin-slow"
        style={{background:`conic-gradient(var(--forest), var(--gold), var(--terra), var(--forest))`, filter:'blur(60px)', transform:'translate(-50%,-50%)'}} />

      <div className="w-full max-w-sm relative z-10 space-y-6">

        {/* Logo — float + bounce-in */}
        <div className="text-center" style={{opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(24px)', transition:'all 0.6s cubic-bezier(0.22,1,0.36,1)'}}>
          <div
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-5 animate-float hover-lift ripple-container"
            style={{background:'var(--forest)', boxShadow:'0 8px 32px rgba(45,90,61,0.35)'}}
          >
            <Wallet className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-3xl font-600" style={{color:'var(--ink)'}}>Smart Ledger</h1>
          <p className="mt-1 text-sm" style={{color:'var(--ink-muted)'}}>家計管理を、もっとスマートに。</p>
        </div>

        {/* Card — slide up with delay */}
        <div
          className={`card p-6 ${shake ? 'animate-wiggle' : ''}`}
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(32px)',
            transition:'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s'
          }}
        >
          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="flex items-start gap-2.5 p-3 rounded-xl animate-bounce-in"
                style={{background:'var(--terra-pale)', color:'var(--terra)'}}>
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 animate-heartbeat" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Username field */}
            <div style={{opacity: mounted ? 1 : 0, transform: mounted ? 'translateX(0)' : 'translateX(-16px)', transition:'all 0.5s cubic-bezier(0.22,1,0.36,1) 0.25s'}}>
              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1.5 transition-colors duration-200"
                style={{color: focusedField==='username' ? 'var(--forest)' : 'var(--ink-muted)'}}>
                Username
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onFocus={() => setFocusedField('username')}
                  onBlur={() => setFocusedField(null)}
                  className="input-warm w-full px-4 py-3 text-sm"
                  placeholder="IDを入力"
                />
                {/* Active border glow */}
                {focusedField === 'username' && (
                  <div className="absolute inset-0 rounded-xl pointer-events-none animate-fade-in"
                    style={{boxShadow:'0 0 0 3px rgba(45,90,61,0.15)'}} />
                )}
              </div>
            </div>

            {/* Password field */}
            <div style={{opacity: mounted ? 1 : 0, transform: mounted ? 'translateX(0)' : 'translateX(-16px)', transition:'all 0.5s cubic-bezier(0.22,1,0.36,1) 0.35s'}}>
              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1.5 transition-colors duration-200"
                style={{color: focusedField==='password' ? 'var(--forest)' : 'var(--ink-muted)'}}>
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="input-warm w-full px-4 py-3 text-sm"
                  placeholder="パスワード"
                />
                {focusedField === 'password' && (
                  <div className="absolute inset-0 rounded-xl pointer-events-none animate-fade-in"
                    style={{boxShadow:'0 0 0 3px rgba(45,90,61,0.15)'}} />
                )}
              </div>
            </div>

            <div className="space-y-3 pt-2" style={{opacity: mounted ? 1 : 0, transition:'opacity 0.5s ease 0.45s'}}>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-forest w-full py-3.5 text-sm flex items-center justify-center gap-2 ripple-container"
                style={{transition:'all 0.2s cubic-bezier(0.22,1,0.36,1)'}}
              >
                {isLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><LogIn className="w-4 h-4" /> ログイン</>
                }
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 dash-divider" />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{color:'var(--ink-muted)'}}>または</span>
                <div className="flex-1 dash-divider" />
              </div>

              <button
                type="button"
                onClick={() => onLoginSuccess('guest')}
                disabled={isLoading}
                className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ripple-container"
                style={{
                  background:'var(--cream-dark)', color:'var(--ink-light)',
                  border:'1.5px solid rgba(44,36,22,0.1)',
                  transition:'all 0.2s cubic-bezier(0.22,1,0.36,1)'
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <User className="w-4 h-4" /> ゲストとして利用
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-[10px]"
          style={{color:'var(--ink-muted)', opacity: mounted ? 1 : 0, transition:'opacity 0.6s ease 0.6s'}}>
          Powered by Gemini AI & Firebase
        </p>
      </div>
    </div>
  );
};
